import { bytesToBase64, downloadDeviceFile, uploadDeviceFile } from '../services/httpTransfer.js';
import { parseThemeJsonl } from './themeModel.js';

function themeFilename(theme) {
    const original = String(theme?.fileName || `${theme?.name || 'theme'}.jsonl`)
        .split(/[\\/]/)
        .pop()
        .trim();
    if (!original) return 'theme.jsonl';
    return /\.jsonl$/i.test(original) ? original : `${original}.jsonl`;
}

export function themeDevicePath(theme) {
    return `L:/themes/${themeFilename(theme)}`;
}

function downloadedText(fileData) {
    const bytes = fileData?.data instanceof Uint8Array
        ? fileData.data
        : new Uint8Array(fileData?.data ?? []);
    return new TextDecoder().decode(bytes);
}

/**
 * Upload the stored source text without reserializing it. The firmware reads
 * the same bytes from /flash/themes, so whitespace and record ordering remain
 * untouched. A strict parse rejects legacy Designer aliases before transfer.
 */
export async function uploadThemeToDevice(
    theme,
    plate,
    {
        upload = uploadDeviceFile,
        download = downloadDeviceFile,
        onPhase = () => {},
    } = {},
) {
    if (!theme?.text || typeof theme.text !== 'string') throw new Error('Theme has no source text');
    if (!plate) throw new Error('No device is selected');

    onPhase({ phase: 'validate', message: 'Validating firmware format…', percent: 5 });
    parseThemeJsonl(theme.text, { normalizeAliases: false });
    const path = themeDevicePath(theme);
    const bytes = new TextEncoder().encode(theme.text);

    onPhase({ phase: 'upload', message: `Uploading ${path}…`, percent: 35 });
    await upload(plate, path, bytesToBase64(bytes));

    onPhase({ phase: 'verify', message: 'Verifying the theme file on the device…', percent: 65 });
    const stored = await download(plate, path);
    const storedText = downloadedText(stored);
    if (storedText !== theme.text) {
        throw new Error(`Device verification failed for ${path}: the stored theme differs from the source file`);
    }

    onPhase({ phase: 'verified', message: 'Theme file verified on the device.', percent: 72 });
    return { path, bytes: bytes.length };
}

export { themeFilename };
